import React from 'react';
import { Grid } from '@mui/material';
import { WIZARD_SELECT_KEYS, WizardSelectKey } from 'src/api/project-settings-keys';
import { GetProjectSettingsResponse } from 'src/model/api/v2/data-contracts';
import { useSetWizardPropertyMutation } from 'src/api/v1/project-settings-ApiSlice';
import { SettingsSection } from 'src/pages/ProjectSettingsPage/components/SettingsSection';
import { SettingsSelect } from 'src/pages/ProjectSettingsPage/components/SettingsSelect';
import { useSaveFeedback } from 'src/pages/ProjectSettingsPage/useSaveFeedback';
import { LIGHTS_WITH_MOTION_INCLUDED } from 'src/components/Planner/planner/wizard-worker-table';
import { ts } from 'src/utils/translate';

type Props = {
    projectId: number;
    settings: GetProjectSettingsResponse;
    canEdit: boolean;
};

function getWizardSelectLabel(key: WizardSelectKey): string {
    switch (key) {
        case 'switch_product_name_id':
            return ts('project_settings_wizard_switches', 'Switches');
        case 'lighting_product_name_id':
            return ts('project_settings_wizard_lighting', 'Lighting');
        case 'shading_product_name_id':
            return ts('project_settings_wizard_shading', 'Shading');
        case 'heating_product_name_id':
            return ts('project_settings_wizard_heating', 'Heating');
        case 'contact_sensor_product_name_id':
            return ts('project_settings_wizard_contact_sensors', 'Contact sensors');
        case 'movement_sensor_product_name_id':
            return ts('project_settings_wizard_movement_sensors', 'Movement sensors');
        case 'wifi_hertz':
            return ts('project_settings_wizard_wifi', 'Wireless LAN');
        case 'ventillation_product_name_id':
            return ts('project_settings_wizard_ventilation', 'Ventilation');
    }
}

export const WizardDevicesSection = ({ projectId, settings, canEdit }: Props) => {
    const [setWizardProperty] = useSetWizardPropertyMutation();
    const { showSaveSuccess, showSaveError } = useSaveFeedback();

    const saveWizardProperty = (key: WizardSelectKey, value: number) => {
        setWizardProperty({ projectId, key, value })
            .unwrap()
            .then(showSaveSuccess)
            .catch(showSaveError);
    };

    const isMotionIncludedInLighting = LIGHTS_WITH_MOTION_INCLUDED.includes(settings.wizard.lighting_product_name_id);

    return (
        <SettingsSection title={ts('project_settings_wizard_title', 'Devices used by the uPlan Wizard')}>
            <Grid container spacing={3}>
                {WIZARD_SELECT_KEYS.map((key) => {
                    const isMotionDisabled = key === 'movement_sensor_product_name_id' && isMotionIncludedInLighting;
                    return (
                        <Grid key={key} size={{ xs: 12, md: 6 }}>
                            <SettingsSelect
                                id={key}
                                label={getWizardSelectLabel(key)}
                                value={settings.wizard[key]}
                                options={settings.options.wizard[key]}
                                disabled={!canEdit || isMotionDisabled}
                                onChange={(value) => saveWizardProperty(key, value)}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </SettingsSection>
    );
};
